import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Pencil, Plus, Rocket, Trash2, Workflow } from 'lucide-react';
import { callsAPI, flowsAPI } from '../../../api/client';
import { useTenant } from '../../../context/TenantContext';
import { apiError } from '../../../lib/host';
import { prettyNumber } from '../CallDialog';
import { ErrorBanner, formatDate, Modal, PageHeading, StatCards, useBasic } from '../ui';
import { TEMPLATES } from './templates';

function NewFlowDialog({ onClose, onCreated, company, team }) {
  const [name, setName] = useState('Main call flow');
  const [template, setTemplate] = useState('standard');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Templates ring real team members, so make sure the team has loaded before building one.
      const members = team.length ? team : (await callsAPI.telephony()).data.team;
      const build = TEMPLATES.find(([k]) => k === template)[3];
      const res = await flowsAPI.create(name.trim(), build(company, members));
      onCreated(res.data);
    } catch (err) {
      setError(apiError(err, 'Could not create the call flow'));
      setBusy(false);
    }
  };

  return (
    <Modal title="New call flow" onClose={onClose} footer={<>
      <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
      <button type="submit" form="new-flow" className="primary-action" disabled={busy}>{busy ? 'Creating…' : 'Create and open builder'}</button>
    </>}>
      <form id="new-flow" className="form-grid" onSubmit={create}>
        <ErrorBanner message={error} />
        <label className="field span-2">Name<input required maxLength={150} value={name} onChange={(e) => setName(e.target.value)} autoFocus /></label>
        <div className="field span-2">Start from
          <div className="template-list">
            {TEMPLATES.map(([key, label, description]) => (
              <label key={key} className={`template-option ${template === key ? 'on' : ''}`}>
                <input type="radio" name="template" value={key} checked={template === key} onChange={() => setTemplate(key)} />
                <span><b>{label}</b><small>{description}</small></span>
              </label>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default function FlowsPage() {
  const navigate = useNavigate();
  const { notify, company } = useBasic();
  const { can } = useTenant();
  const canEdit = can('flows.write');
  const [flows, setFlows] = useState(null);
  const [team, setTeam] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    flowsAPI.list().then((res) => setFlows(res.data)).catch((err) => setError(apiError(err, 'Could not load call flows')));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { callsAPI.telephony().then((res) => setTeam(res.data.team)).catch(() => {}); }, []);

  const run = async (fn, ok) => {
    setError('');
    try {
      const res = await fn();
      notify(typeof ok === 'function' ? ok(res.data) : ok);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail?.errors ? `${detail.message}: ${detail.errors.join(' · ')}` : apiError(err));
    }
  };

  const live = flows?.filter((f) => f.is_published) || [];
  const answers = (f) => (f.is_published ? f.number : f.draft_number);

  return (
    <section className="page-view">
      <PageHeading title="Call Flows" subtitle="Design what callers hear, which keys they press, and who answers.">
        {canEdit && <button className="primary-action" onClick={() => setCreating(true)}><Plus size={14} />New call flow</button>}
      </PageHeading>
      <ErrorBanner message={error} />

      <StatCards stats={[
        ['Call flows', flows?.length, 'Designed for your company'],
        ['Live', flows ? live.length : null, 'Answering real calls'],
        ['Numbers covered', flows ? (live.some((f) => !f.number) ? 'All' : live.length) : null, live.some((f) => !f.number) ? 'A flow covers every number' : 'By a live flow'],
        ['Drafts', flows ? flows.length - live.length : null, 'Not answering calls yet'],
      ]} />

      <section className="panel page-table-panel">
        <div className="panel-heading"><div><h2>Your call flows</h2><small className="table-subtitle">{flows === null ? 'Loading…' : `${flows.length} flow${flows.length === 1 ? '' : 's'}`}</small></div></div>
        <div className="table-scroll">
          <table className="full-page-table">
            <thead><tr><th>Name</th><th>Answers</th><th>Status</th><th>Blocks</th><th>Last edited</th><th>Action</th></tr></thead>
            <tbody>
              {flows?.map((f) => (
                <tr key={f.id}>
                  <td><button className="link-button cell-strong" onClick={() => navigate(`/call-flows/${f.id}`)}>{f.name}</button></td>
                  <td>{answers(f) ? prettyNumber(answers(f)) : 'All company numbers'}</td>
                  <td>
                    {f.is_published
                      ? <span className={`status ${f.has_unpublished_changes ? 'pending' : 'green'}`}>{f.has_unpublished_changes ? 'Live · changes not published' : 'Live'}</span>
                      : <span className="status blue">Draft</span>}
                  </td>
                  <td>{f.blocks}</td>
                  <td>{formatDate(f.updated_at)}</td>
                  <td>
                    <div className="row-actions">
                      <button className="row-action" onClick={() => navigate(`/call-flows/${f.id}`)}><Pencil size={12} />{canEdit ? 'Open builder' : 'View'}</button>
                      {canEdit && !f.is_published && (
                        <button className="row-action call" onClick={() => run(() => flowsAPI.publish(f.id), (d) => (d.replaced.length ? `${f.name} is live, replacing ${d.replaced.join(', ')}.` : `${f.name} is live.`))}>
                          <Rocket size={12} />Publish
                        </button>
                      )}
                      {canEdit && f.is_published && (
                        <button className="row-action" onClick={() => window.confirm(`Stop using "${f.name}" for real calls?`) && run(() => flowsAPI.unpublish(f.id), `${f.name} is no longer live.`)}>Unpublish</button>
                      )}
                      {canEdit && (
                        <button className="row-action danger" aria-label={`Delete ${f.name}`} onClick={() => window.confirm(`Delete "${f.name}"? This can't be undone.`) && run(() => flowsAPI.remove(f.id), `${f.name} deleted.`)}><Trash2 size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {flows?.length === 0 && (
                <tr><td colSpan={6} className="empty-state">
                  No call flows yet. Calls follow your standard routing on the Phone Numbers page.
                  {canEdit && <> <button className="link-button" onClick={() => setCreating(true)}>Create your first call flow</button></>}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="page-bottom-grid">
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>How a call is answered</h2></div>
          <ol className="answer-order">
            <li>A <b>live flow</b> set to that number</li>
            <li>Otherwise, a live flow set to <b>All company numbers</b></li>
            <li>Otherwise, the <b>standard routing</b> on Phone Numbers</li>
          </ol>
        </section>
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Who can change flows</h2><Workflow size={16} /></div>
          <p className="access-copy">Only the company owner can create, edit and publish call flows. Everyone else can open them and test them in the simulator.</p>
          <button className="upgrade-full" onClick={() => navigate('/phone-numbers')}>Phone numbers & standard routing <ArrowRight size={14} /></button>
        </section>
      </div>

      {creating && <NewFlowDialog company={company} team={team} onClose={() => setCreating(false)} onCreated={(f) => navigate(`/call-flows/${f.id}`)} />}
    </section>
  );
}
